/**
 * Print pipeline for the File ▸ Print pane.
 * Pages are rasterised with the same renderer as Export, laid out on sheets of the chosen paper size inside a hidden
 * iframe (CSS `@page { size }` + one <img> per sheet) and handed to the browser's print dialog.
 */
import { pageRenderSize, marksEnabled, MARKS_BLEED, normalizeBleed } from './renderUtils';
import { buildSheets, buildBookletSheets } from './spreads';
import { renderSheetToCanvas } from './sheetRender';

export const PX_PER_IN = 96;
const MM = 1 / 25.4; // inches per millimetre

export const PAPER_SIZES = [
  { id: 'publication', label: 'Same as publication' },
  { id: 'letter', label: 'Letter (8.5 × 11 in)', w: 8.5, h: 11 },
  { id: 'legal', label: 'Legal (8.5 × 14 in)', w: 8.5, h: 14 },
  { id: 'tabloid', label: 'Tabloid (11 × 17 in)', w: 11, h: 17 },
  { id: 'a3', label: 'A3 (297 × 420 mm)', w: 297 * MM, h: 420 * MM },
  { id: 'a4', label: 'A4 (210 × 297 mm)', w: 210 * MM, h: 297 * MM },
  { id: 'a5', label: 'A5 (148 × 210 mm)', w: 148 * MM, h: 210 * MM },
  { id: 'postcard', label: 'Postcard (6 × 4 in)', w: 6, h: 4 },
];

export const DEFAULT_PRINT_OPTIONS = {
  copies: 1,
  range: 'all', // all | current | custom
  customRange: '',
  sides: 'one', // one | two (hint only — duplex is chosen in the browser dialog)
  paper: 'publication',
  orientation: 'auto', // auto | portrait | landscape
  scaling: 'actual', // actual | fit
  quality: 2, // render scale (2 = 192 dpi)
  cropMarks: false, // printer's marks: trim lines at the page corners (adds a white bleed around the page)
  foldMarks: false, // dashed marks at every fold guide
  bleed: MARKS_BLEED, // bleed width in px (12 = 1/8 in, 24 = 1/4 in, 36 = 3/8 in) used when marks are on
  layout: 'pages', // pages | spreads | booklet — one page per sheet, facing pages side by side, or saddle-stitch imposition
  bookletSides: 'both', // booklet only: both (interleaved for duplex, flip on the short edge) | front | back (manual two-sided)
};

/** The layout that will actually be used (spreads / booklet need at least two pages). */
export const effectiveLayout = (opts, pageCount) => (pageCount > 1 && (opts.layout === 'spreads' || opts.layout === 'booklet') ? opts.layout : 'pages');

/** Marks descriptor for renderUtils from print options (+ the publication's guides). */
export const printMarks = (opts, guides = []) => (opts.cropMarks || opts.foldMarks ? { crop: !!opts.cropMarks, fold: !!opts.foldMarks, bleed: normalizeBleed(opts.bleed), guides } : null);

/** The page as it will be rasterised: the page itself, or page + bleed when marks are on. */
export const effectivePage = (page, opts, guides = []) => {
  const m = printMarks(opts, guides);
  if (!marksEnabled(m)) return page;
  const { width, height } = pageRenderSize(page, m);
  return { ...page, width, height };
};

/** "1-3, 5, 8-" → [0, 1, 2, 4, 7, …] (0-based, deduplicated, in order). Returns [] when nothing is valid. */
export const parsePageRange = (text, pageCount) => {
  const out = [];
  const seen = new Set();
  const push = (n) => { if (n >= 1 && n <= pageCount && !seen.has(n)) { seen.add(n); out.push(n - 1); } };
  String(text || '').split(/[,;\s]+/).filter(Boolean).forEach((part) => {
    const m = part.match(/^(\d+)?\s*-\s*(\d+)?$/);
    if (m && (m[1] || m[2])) {
      const a = m[1] ? parseInt(m[1], 10) : 1;
      const b = m[2] ? parseInt(m[2], 10) : pageCount;
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      for (let n = lo; n <= hi; n++) push(n);
    } else if (/^\d+$/.test(part)) {
      push(parseInt(part, 10));
    }
  });
  return out;
};

/** Indices of the pages that will be printed for the given options. */
export const resolvePrintPages = (pages, currentPageIndex, opts) => {
  if (opts.range === 'current') return [Math.max(0, Math.min(pages.length - 1, currentPageIndex))];
  if (opts.range === 'custom') return parsePageRange(opts.customRange, pages.length);
  return pages.map((_, i) => i);
};

/**
 * The sheets that will be printed: one per page, or — layout 'spreads' — one per two-page spread (a spread is printed
 * when either of its pages is in the range, so "current page" prints the spread on screen). See lib/spreads.buildSheets.
 */
export const resolvePrintSheets = (pages, currentPageIndex, opts) => {
  const layout = effectiveLayout(opts, pages.length);
  if (layout === 'booklet') return buildBookletSheets(pages, opts.bookletSides); // a booklet is always the whole publication
  return buildSheets(pages, resolvePrintPages(pages, currentPageIndex, opts), layout === 'spreads' ? 'spread' : 'single');
};

/** Sheet (paper) size in inches for a page under the given options. */
export const sheetSizeFor = (page, opts) => {
  const pw = page.width / PX_PER_IN;
  const ph = page.height / PX_PER_IN;
  const paper = PAPER_SIZES.find((p) => p.id === opts.paper);
  let w = pw, h = ph;
  if (paper && paper.w) { w = paper.w; h = paper.h; }
  const wantLandscape = opts.orientation === 'landscape' || (opts.orientation === 'auto' && pw > ph);
  if (paper && paper.w) {
    if (wantLandscape && w < h) [w, h] = [h, w];
    if (!wantLandscape && w > h) [w, h] = [h, w];
  }
  return { w, h };
};

/** Where the page image sits on the sheet (inches). Actual size is centred; "fit" scales to the sheet. */
export const placementFor = (page, opts) => {
  const sheet = sheetSizeFor(page, opts);
  const pw = page.width / PX_PER_IN;
  const ph = page.height / PX_PER_IN;
  let scale = 1;
  if (opts.scaling === 'fit') scale = Math.min(sheet.w / pw, sheet.h / ph);
  else if (pw > sheet.w + 1e-6 || ph > sheet.h + 1e-6) scale = Math.min(sheet.w / pw, sheet.h / ph); // never clip
  const w = pw * scale, h = ph * scale;
  return { sheet, x: (sheet.w - w) / 2, y: (sheet.h - h) / 2, w, h, scale, clipped: false };
};

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Build the standalone HTML document that is printed. `sheets` = [{ src, sheet: {w,h}, x, y, w, h }]. */
export const buildPrintHtml = (title, sheets) => {
  const first = sheets[0];
  // margin: 0 leaves the browser no room for its own header / footer (date, title, URL); Chrome 131+ also honours the
  // @page margin boxes, so switch them off explicitly as well.
  const marginBoxes = ['top-left-corner', 'top-left', 'top-center', 'top-right', 'top-right-corner', 'left-top', 'left-middle', 'left-bottom', 'right-top', 'right-middle', 'right-bottom', 'bottom-left-corner', 'bottom-left', 'bottom-center', 'bottom-right', 'bottom-right-corner'].map((b) => `@${b} { content: none; }`).join(' ');
  const pageRule = first ? `@page { size: ${first.sheet.w.toFixed(3)}in ${first.sheet.h.toFixed(3)}in; margin: 0; ${marginBoxes} }` : `@page { margin: 0; ${marginBoxes} }`;
  const body = sheets.map((s, i) => (
    `<div class="sheet" style="width:${s.sheet.w.toFixed(3)}in;height:${s.sheet.h.toFixed(3)}in" data-sheet="${i + 1}">` +
    `<img src="${s.src}" alt="${esc(s.label || `Page ${i + 1}`)}" style="left:${s.x.toFixed(3)}in;top:${s.y.toFixed(3)}in;width:${s.w.toFixed(3)}in;height:${s.h.toFixed(3)}in" /></div>`
  )).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
${pageRule}
html,body{margin:0;padding:0;background:#fff}
.sheet{position:relative;overflow:hidden;page-break-after:always;break-after:page;background:#fff}
.sheet:last-child{page-break-after:auto;break-after:auto}
.sheet img{position:absolute;display:block}
@media screen{body{background:#666;padding:12px}.sheet{margin:0 auto 12px;box-shadow:0 2px 8px rgba(0,0,0,.4)}}
</style></head><body>${body}</body></html>`;
};

/**
 * Render the sheets exactly as printPublication lays them out and return them as a jsPDF document — the "print to PDF"
 * path that never goes through the browser's print dialog (and so never gets its date / title / URL header and footer).
 */
export const printToPdf = async ({ pages, masters = {}, textFlow = {}, guides = [], currentPageIndex = 0, options = DEFAULT_PRINT_OPTIONS, onProgress }) => {
  const { default: JsPDF } = await import('jspdf');
  const opts = { ...DEFAULT_PRINT_OPTIONS, ...options };
  const sheetsToPrint = resolvePrintSheets(pages, currentPageIndex, opts);
  if (!sheetsToPrint.length) throw new Error('No pages match the page range.');
  const marks = printMarks(opts, guides);
  const IN = 25.4; // mm per inch
  let pdf = null;
  const copies = Math.max(1, Math.min(99, Math.round(opts.copies) || 1));
  const rendered = [];
  for (let i = 0; i < sheetsToPrint.length; i++) {
    const sh = sheetsToPrint[i];
    onProgress && onProgress(i + 1, sheetsToPrint.length);
    const canvas = await renderSheetToCanvas(sh, masters, textFlow, opts.quality, marks);
    rendered.push({ src: canvas.toDataURL('image/jpeg', 0.95), ...placementFor(effectivePage(sh, opts, guides), opts) });
  }
  for (let c = 0; c < copies; c++) {
    for (const s of rendered) {
      const format = [s.sheet.w * IN, s.sheet.h * IN];
      const orientation = s.sheet.w > s.sheet.h ? 'landscape' : 'portrait';
      if (!pdf) pdf = new JsPDF({ unit: 'mm', format, orientation, compress: true });
      else pdf.addPage(format, orientation);
      pdf.addImage(s.src, 'JPEG', s.x * IN, s.y * IN, s.w * IN, s.h * IN);
    }
  }
  return { pdf, sheets: rendered.length * copies, pages: sheetsToPrint.flatMap((sh) => sh.pageIndexes.map((i) => i + 1)), layout: effectiveLayout(opts, pages.length), booklet: sheetsToPrint[0]?.booklet ? { sheets: sheetsToPrint[0].booklet.sheets, blanks: sheetsToPrint[0].booklet.blanks, sides: opts.bookletSides } : null };
};

/**
 * Render the selected pages and open the browser print dialog.
 * Returns the job description ({ sheets, html }) so callers/tests can inspect what was sent.
 */
export const printPublication = async ({ pages, masters = {}, textFlow = {}, guides = [], currentPageIndex = 0, title = 'Publication', options = DEFAULT_PRINT_OPTIONS, onProgress }) => {
  const opts = { ...DEFAULT_PRINT_OPTIONS, ...options };
  const sheetsToPrint = resolvePrintSheets(pages, currentPageIndex, opts);
  if (!sheetsToPrint.length) throw new Error('No pages match the page range.');
  const marks = printMarks(opts, guides);
  const rendered = [];
  for (let i = 0; i < sheetsToPrint.length; i++) {
    const sh = sheetsToPrint[i];
    onProgress && onProgress(i + 1, sheetsToPrint.length);
    const canvas = await renderSheetToCanvas(sh, masters, textFlow, opts.quality, marks);
    const place = placementFor(effectivePage(sh, opts, guides), opts);
    rendered.push({ src: canvas.toDataURL('image/jpeg', 0.95), ...place, pageIndex: sh.pageIndexes[0], label: sh.label });
  }
  const copies = Math.max(1, Math.min(99, Math.round(opts.copies) || 1));
  const sheets = [];
  for (let c = 0; c < copies; c++) sheets.push(...rendered);
  const html = buildPrintHtml(title, sheets);
  const job = { sheets: sheets.length, pages: sheetsToPrint.flatMap((sh) => sh.pageIndexes.map((i) => i + 1)), layout: effectiveLayout(opts, pages.length), spreads: sheetsToPrint.map((sh) => sh.label), booklet: sheetsToPrint[0]?.booklet ? { sheets: sheetsToPrint[0].booklet.sheets, blanks: sheetsToPrint[0].booklet.blanks, sides: opts.bookletSides } : null, copies, paper: rendered[0].sheet, marks: marks ? { crop: marks.crop, fold: marks.fold, bleed: marks.bleed } : null, html };
  if (typeof window !== 'undefined') window.__wpLastPrintJob = job; // test / debugging hook

  await new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'Print preview';
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(frame);
    const cleanup = () => { setTimeout(() => frame.remove(), 1000); resolve(); };
    const doc = frame.contentDocument || frame.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const imgs = Array.from(doc.images);
    const ready = Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((r) => { img.onload = r; img.onerror = r; }))));
    ready.then(() => {
      const win = frame.contentWindow;
      let done = false;
      const finish = () => { if (!done) { done = true; cleanup(); } };
      win.addEventListener('afterprint', finish);
      try {
        win.focus();
        win.print();
      } catch (e) {
        console.warn('print() failed', e);
      }
      // Browsers that block print() (or return immediately) still get cleaned up.
      setTimeout(finish, 60000);
    });
  });
  return job;
};
